import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'

import { db } from '../FirebaseInit'
import { useToast } from '../context/ToastContext'

/**
 * useTicket
 *
 * Subscribes to a single ticket document. Returns the ticket as
 * `{ id, ...docData }` or `null` when the document does not exist.
 *
 * @param {string} ticketId The ticket document id.
 * @returns {{ data: (Object|null), loading: boolean, error: (Error|null) }}
 */
const useTicket = (ticketId) => {
  const { showToast } = useToast()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!ticketId) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)

    const ticketRef = doc(db, 'tickets', ticketId)

    const unsubscribe = onSnapshot(
      ticketRef,
      (snapshot) => {
        setData(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null)
        setError(null)
        setLoading(false)
      },
      (err) => {
        if (err.code === 'permission-denied') {
          showToast('You don\'t have access to this ticket', 'red')
        }
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [ticketId, showToast])

  return { data, loading, error }
}

export default useTicket
